import { TickerBadge } from "@/components/site/FundBits";
import { money, pct, aum, fee, distAmt } from "@/lib/api";
import { t } from "@/lib/i18n";

const perfVal = (r, k) => (r.perf && r.perf[k] != null ? `${r.perf[k].toFixed(2)}%` : "—");

export const CompareTable = ({ data, lang }) => {
  const s = t(lang);
  const rows = data.rows;
  const perfAsAt = rows.find((r) => r.perf?.as_at)?.perf.as_at;
  const metrics = [
    [s.price, (r) => money(r.market_price)], [s.nav, (r) => money(r.nav)],
    [s.yield, (r) => (r.current_yield_pct != null ? pct(r.current_yield_pct) : "—")],
    [s.lastDist, (r) => distAmt(r.last_distribution)], [s.freq, (r) => r.distribution_frequency],
    [s.aum, (r) => aum(r.aum_m)], [s.risk, (r) => r.risk_rating], [s.fee, (r) => fee(r.mgmt_fee_pct)],
    ["1Y", (r) => perfVal(r, "1Y")], ["3Y", (r) => perfVal(r, "3Y")], ["5Y", (r) => perfVal(r, "5Y")], ["SI", (r) => perfVal(r, "SI")],
  ];
  return (
    <section className="hv-rise border border-hv-rule bg-white" aria-label="Fund comparison" data-testid="compare-table">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12px]">
          <caption className="sr-only">Comparison of {rows.map((r) => r.ticker).join(", ")}</caption>
          <thead>
            <tr className="border-b border-hv-rule">
              <th scope="col" className="p-2 text-left font-heading text-[10px] font-semibold uppercase tracking-wider text-hv-muted">Metric</th>
              {rows.map((r) => (
                <th key={r.ticker} scope="col" className="p-2 text-left align-bottom">
                  <TickerBadge ticker={r.ticker} brand={r.fund_name.includes("High Income Shares") ? "High Income Shares" : "Harvest ETFs"} className="h-6" />
                  <span className="mt-1 block font-heading text-[11px] font-semibold leading-tight text-hv-text">{r.fund_name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map(([label, fn], i) => (
              <tr key={label} className={i === 8 ? "border-t-2 border-hv-rule" : "border-t border-hv-rule"}>
                <th scope="row" className="whitespace-nowrap p-2 text-left font-heading text-[11px] font-semibold text-hv-muted">{label}</th>
                {rows.map((r) => <td key={r.ticker} className="p-2 font-heading font-semibold text-hv-navy">{fn(r)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.some((r) => r.leverage_note) && (
        <p className="border-t border-hv-rule px-3 py-2 text-[11px] text-hv-text" data-testid="compare-leverage-note">
          <strong className="text-hv-navy">Leverage:</strong> {rows.filter((r) => r.leverage_note).map((r) => r.ticker).join(", ")} uses approximately 25% leverage; borrowing costs apply.
        </p>
      )}
      <p className="border-t border-hv-rule px-3 py-2 text-[11px] text-hv-muted">
        {s.asAt} {rows[0]?.as_at} ({s.endOfDay}). {s.perf}{perfAsAt ? ` ${s.asAt.toLowerCase()} ${perfAsAt}` : ""}. {s.pastPerf}
      </p>
    </section>
  );
};

export const FundList = ({ data, lang }) => {
  const s = t(lang);
  return (
    <section className="hv-rise border border-hv-rule bg-white" aria-label="Fund list" data-testid="fund-list">
      <ul className="divide-y divide-hv-rule">
        {data.items.map((f) => (
          <li key={f.ticker} className="flex items-center gap-3 p-2.5">
            <span className="flex w-14 shrink-0 items-center"><TickerBadge ticker={f.ticker} brand={f.brand} className="h-5 max-w-full object-contain object-left" /></span>
            <a href={f.page_url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate font-heading text-[12px] font-semibold text-hv-text hover:underline">{f.fund_name}</a>
            <span className="whitespace-nowrap text-right font-heading text-[11px] text-hv-navy">
              {f.current_yield_pct != null ? pct(f.current_yield_pct) : "—"} · {f.risk_rating}
            </span>
          </li>
        ))}
      </ul>
      <p className="border-t border-hv-rule px-3 py-2 text-[11px] text-hv-muted">
        {data.items.length} / {data.total} {s.funds} · {s.yield} · {s.risk} · {s.asAt} {data.items[0]?.as_at} ({s.endOfDay})
      </p>
    </section>
  );
};
